import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bug, Search, X } from 'lucide-react';
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@/components/primitives';
import useResource from '@/pages/dashboard/useResource';
import { apiGet } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';
import { PriorityBadge } from '@/pages/dashboard/VulnBrief';
import useMediaQuery from '@/hooks/useMediaQuery';
import useVulnFilters, { DAYS, PRIORITIES, SCOPES, SEVERITIES, SORT_LABELS, TOGGLES } from '@/pages/vuln/useVulnFilters';
import VulnTable from '@/pages/vuln/VulnTable';
import VulnPanel from '@/pages/vuln/VulnPanel';
import '@/styles/vuln.css';
import '@/styles/vuln-page.css';

const PAGE = 50;
const n = (v) => (v == null ? 0 : Number(v).toLocaleString());

/*
 * Vulnerabilities: the CVEs in your threats plus the public catalog (CISA KEV, new High and Critical CVEs,
 * trending). Sorting and filtering happen on the server, the filters live in the URL, and a CVE opens in a
 * panel at the right of the list with its explanation, remediation and threat hunting queries.
 */
export default function Vulnerabilities() {
  const { filters, set, clear, active, query } = useVulnFilters();
  const isWide = useMediaQuery('(min-width: 1280px)');
  const isPhone = useMediaQuery('(max-width: 767.98px)');
  const [limit, setLimit] = useState(PAGE);
  const [search, setSearch] = useState(filters.q);
  const headingRef = useRef(null);
  const lastData = useRef(null);

  // The limit starts over whenever the view changes.
  const viewKey = JSON.stringify(query);
  useEffect(() => { setLimit(PAGE); }, [viewKey]);

  // Typing waits a moment before it queries.
  useEffect(() => { setSearch(filters.q); }, [filters.q]);
  useEffect(() => {
    if (search === filters.q) return undefined;
    const t = setTimeout(() => set({ q: search }), 280);
    return () => clearTimeout(t);
  }, [search, filters.q, set]);

  const list = useResource(() => apiGet('/dashboard/vulns', { ...query, limit }), [viewKey, limit], { poll: false });
  if (list.data) lastData.current = list.data;
  const data = list.data || lastData.current;
  const refreshing = list.loading && Boolean(lastData.current);

  const rows = data?.rows || [];
  const selectedIndex = filters.cve ? rows.findIndex((r) => r.cve_id === filters.cve) : -1;
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : null;
  const panelMode = isPhone ? 'screen' : isWide ? 'side' : 'drawer';
  const showPanel = Boolean(filters.cve);

  const open = useCallback((cve, tab) => set({ cve, tab: tab || '' }), [set]);
  const close = useCallback(() => set({ cve: '', tab: '' }), [set]);
  const step = useCallback((d) => {
    const next = rows[selectedIndex + d];
    if (next) set({ cve: next.cve_id, tab: filters.tab });
  }, [rows, selectedIndex, set, filters.tab]);

  // j / k move through the list, Esc closes the panel.
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable) return;
      if (e.key === 'Escape' && showPanel) { close(); return; }
      if (!rows.length) return;
      if (e.key === 'j') { if (selectedIndex < 0) open(rows[0].cve_id); else step(1); }
      if (e.key === 'k' && selectedIndex > 0) step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, selectedIndex, showPanel, open, close, step]);

  useEffect(() => { if (showPanel && panelMode !== 'side') headingRef.current?.focus({ preventScroll: true }); }, [filters.cve, showPanel, panelMode]);

  const facets = data?.facets;
  const scopes = data?.scopes || {};
  const sort = filters.sort || data?.sort || 'priority';
  const toggleIn = (key, value) => set({ [key]: filters[key].includes(value) ? filters[key].filter((x) => x !== value) : [...filters[key], value] });

  let body;
  if (!data) {
    body = list.error
      ? <ErrorState title="Couldn't load vulnerabilities" error={list.error} onRetry={list.reload} />
      : <LoadingState label="vulnerabilities" rows={8} />;
  } else if (!rows.length) {
    body = (
      <EmptyState
        icon={active ? Search : Bug}
        title={active ? 'No CVEs match these filters' : filters.scope === 'mine' ? 'No CVEs in your threats yet' : 'Nothing here yet'}
        description={active ? 'Clear a filter, or look at a wider list such as Everything.'
          : filters.scope === 'mine' ? 'CVEs cited by threats matched to your profile appear here. The Exploited, New and Trending lists cover the wider catalog.'
            : 'The catalog is still loading. Try again in a minute.'}
        action={active ? <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={clear}>Clear filters</button> : null}
      />
    );
  } else {
    body = (
      <>
        <VulnTable rows={rows} selectedId={filters.cve} onOpen={open} docked={showPanel && panelMode === 'side'} scope={filters.scope} />
        <div className="vp-foot">
          <span>Showing {rows.length} of {n(data.total)}</span>
          {data.total > rows.length && (
            <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => setLimit((l) => l + PAGE)} disabled={refreshing}>
              Show {Math.min(PAGE, data.total - rows.length)} more
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <div className={`vp-page ${showPanel ? `has-panel has-panel--${panelMode}` : ''}`} data-testid="vulnerabilities-page">
      <div className="vp-main">
        <PageHeader
          title="Vulnerabilities"
          subtitle={data ? `${n(data.total)} ${data.total === 1 ? 'CVE' : 'CVEs'} in this view. Prioritised with CISA's SSVC decision tree; open one for its explanation, remediation and threat hunting queries.` : 'Prioritised CVEs with explanation, remediation and threat hunting queries.'}
        />

        <div className="vp-views" role="tablist" aria-label="Which CVEs">
          {SCOPES.map((s) => (
            <button key={s.key} type="button" role="tab" aria-selected={filters.scope === s.key} title={s.hint} data-testid={`vscope-${s.key}`}
              onClick={() => set({ scope: s.key === 'mine' ? '' : s.key, sort: '', days: s.key === 'new' ? '' : filters.days }, { keepPanel: true })}>
              {s.label} <span className="vp-n">{n(scopes[s.key])}</span>
            </button>
          ))}
          {data?.catalog?.refreshed_at && <span className="vp-fresh">Catalog updated {ago(data.catalog.refreshed_at)}</span>}
        </div>

        <div className="vp-toolbar" role="search">
          <label className="vp-search">
            <Search aria-hidden="true" />
            <span className="sr-only">Search CVEs</span>
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search CVE, vendor, product or threat" />
          </label>
          <label className="vp-field">
            <span>Sort</span>
            <select value={sort} onChange={(e) => set({ sort: e.target.value })} aria-label="Sort by">
              {Object.entries(data?.sorts || SORT_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </label>
          {filters.scope !== 'new' && (
            <label className="vp-field">
              <span>Published</span>
              <select value={filters.days} onChange={(e) => set({ days: e.target.value })} aria-label="Published within">
                {DAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
          )}
          <label className="vp-field">
            <span>Vendor</span>
            <select value={filters.vendor} onChange={(e) => set({ vendor: e.target.value })} aria-label="Vendor">
              <option value="">Any vendor</option>
              {filters.vendor && !(facets?.vendors || []).some((v) => v.name === filters.vendor) && <option value={filters.vendor}>{filters.vendor}</option>}
              {(facets?.vendors || []).map((v) => <option key={v.name} value={v.name}>{v.name} ({v.count})</option>)}
            </select>
          </label>
          {active && <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={clear}><X aria-hidden="true" /> Clear filters</button>}
        </div>

        <div className="vp-filters" aria-label="Filters">
          <div className="vp-chipgroup" role="group" aria-label="Priority">
            <span className="vp-chiplabel">Priority</span>
            {PRIORITIES.map((p) => (
              <button key={p} type="button" className="vp-chip" aria-pressed={filters.priority.includes(p)} onClick={() => toggleIn('priority', p)}>
                <PriorityBadge decision={p} /> <span className="vp-n">{n(facets?.priority?.[p])}</span>
              </button>
            ))}
          </div>
          <div className="vp-chipgroup" role="group" aria-label="CVSS severity">
            <span className="vp-chiplabel">Severity</span>
            {SEVERITIES.map((s) => (
              <button key={s} type="button" className="vp-chip" aria-pressed={filters.severity.includes(s)} onClick={() => toggleIn('severity', s)}>
                {s} <span className="vp-n">{n(facets?.severity?.[s])}</span>
              </button>
            ))}
          </div>
          <div className="vp-chipgroup" role="group" aria-label="Signals">
            <span className="vp-chiplabel">Signals</span>
            {TOGGLES.map((t) => (
              <button key={t.key} type="button" className="vp-chip" aria-pressed={filters[t.key]} title={t.hint} onClick={() => set({ [t.key]: !filters[t.key] })}>
                {t.label} <span className="vp-n">{n(facets?.[t.facet])}</span>
              </button>
            ))}
          </div>
        </div>

        {list.error && data && <ErrorState variant="inline" title="Couldn't refresh the list" description="Showing the last list that loaded." error={list.error} onRetry={list.reload} />}
        <section className={`vp-surface ${refreshing ? 'is-busy' : ''}`} aria-busy={refreshing} aria-label="CVE list">{body}</section>
        {data && !data.catalog?.kev_available && <p className="t-small vp-note">The CISA KEV catalog could not be loaded, so exploitation flags are incomplete.</p>}
        <p className="t-small vp-note">
          {n(data?.catalog?.cves)} CVEs in the public catalog. Sources: NVD, CISA KEV, FIRST EPSS, OSV.dev, MITRE CWE and ATT&amp;CK, Exploit-DB and Nuclei indexes.
        </p>
      </div>

      {showPanel && panelMode === 'drawer' && <div className="vp-scrim" onClick={close} aria-hidden="true" />}
      {showPanel && (
        <VulnPanel
          ref={headingRef} row={selectedRow} cveId={filters.cve} tab={filters.tab} onTab={(t) => set({ tab: t === 'overview' ? '' : t })}
          onClose={close} onStep={step} canPrev={selectedIndex > 0} canNext={selectedIndex >= 0 && selectedIndex < rows.length - 1} mode={panelMode}
        />
      )}
    </div>
  );
}
